import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import Modal from "../../Modal/Modal";

import "./ClanMembersTab.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
// 💡 UPDATED: Role hierarchy now includes Guest. Note: The backend will validate the promotion path.
const ROLE_HIERARCHY = ["Guest", "Member", "Admin", "Owner"];
const PAGE_SIZE_OPTIONS = [10, 25, 50];

// 💡 Helper Function for Short Local Date Formatting (e.g., 10/3/2025 or 3/10/2025)
const formatLocalDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    // toLocaleDateString() without options uses the default short format
    // (MM/DD/YYYY, DD/MM/YYYY, etc.) based on the user's browser locale.
    return date.toLocaleDateString();
  } catch (e) {
    console.error("Date formatting failed:", e);
    return dateString.split("T")[0] || "N/A"; // Fallback
  }
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#2a2a2a",
    borderColor: state.isFocused ? "#6366f1" : "#444",
    color: "#fff",
    padding: "2px",
    boxShadow: "none",
    "&:hover": { borderColor: "#6366f1" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#2a2a2a",
    border: "1px solid #444",
    marginTop: "2px",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "160px",
    overflowY: "auto",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#2a2a2a",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#444",
      borderRadius: "4px",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#444" : "#2a2a2a",
    color: "#fff",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  input: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#666",
  }),
};

function ClanMembersTab({
  clanId,
  members,
  updateMembersList,
  currentUserId,
  isCurrentUserManager,
  isCurrentUserOwner,
  isActionInProgress,
  totalManagers,
  clanName,
  refreshUserData,
  navigate,
  getErrorMessage,
  fetchClanData,
  setIsRedirecting,
  setActionMessage,
  setIsProcessingAction,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isKickModalOpen, setIsKickModalOpen] = useState(false);
  const [targetMember, setTargetMember] = useState(null);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [newMemberData, setNewMemberData] = useState({
    user_id: "",
    role: "Guest",
  });
  const [selectedNewMemberOption, setSelectedNewMemberOption] = useState(null);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  const handleLeaveClan = () => {
    setActionMessage(null);
    setIsLeaveModalOpen(true);
  };

  const executeLeaveClan = async () => {
    setIsLeaveModalOpen(false);
    setActionMessage({
      type: "success",
      message: (
        <>
          Successfully left <b>{clanName}</b>. Redirecting...
        </>
      ),
    });
    setIsProcessingAction(true);

    setTimeout(async () => {
      try {
        await axios.delete(
          `${API_BASE_URL}/clan/${clanId}/members/${currentUserId}`,
          { withCredentials: true },
        );

        if (refreshUserData) {
          await refreshUserData();
        }

        setIsRedirecting(true);
        navigate("/profile");
      } catch (err) {
        console.error("Leave clan failed:", err);
        let errorMessage =
          "Failed to leave clan. Check if you are the sole Owner/Admin. Please retry.";
        if (err.response && err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        }

        setActionMessage({ type: "error", message: errorMessage });
        setIsProcessingAction(false);
        setIsRedirecting(false);
        await fetchClanData();
      }
    }, 500);
  };

  const handleKickMember = (memberId, username) => {
    setTargetMember({ id: memberId, username: username });
    setIsKickModalOpen(true);
  };

  const executeKick = async () => {
    if (!targetMember) return;

    setIsKickModalOpen(false);
    setActionMessage(null);
    setIsProcessingAction(true);

    try {
      await axios.delete(
        `${API_BASE_URL}/clan/${clanId}/members/${targetMember.id}`,
        { withCredentials: true },
      );

      setActionMessage({
        type: "success",
        message: `${targetMember.username} was successfully kicked.`,
      });

      const updatedMembers = members.filter(
        (m) => Number(m.member_id) !== Number(targetMember.id),
      );
      updateMembersList(updatedMembers);
    } catch (err) {
      console.error("Kick member failed:", err);
      const errorMessage = getErrorMessage(
        err,
        "Failed to kick member. Please check permissions.",
      );
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setTargetMember(null);
      setIsProcessingAction(false);
    }
  };

  const handleUpdateRole = async (member, action) => {
    setActionMessage(null);
    const currentRoleIndex = ROLE_HIERARCHY.indexOf(member.role);
    const targetUserId = Number(member.member_id);

    let newRoleIndex;
    if (action === "promote") {
      newRoleIndex = currentRoleIndex + 1;
    } else {
      newRoleIndex = currentRoleIndex - 1;
    }

    if (newRoleIndex < 0 || newRoleIndex >= ROLE_HIERARCHY.length) {
      const message =
        action === "promote"
          ? `Cannot promote ${member.username} further than Owner.`
          : `Cannot demote ${member.username} further than Guest.`;
      setActionMessage({ type: "error", message });
      return;
    }

    const newRole = ROLE_HIERARCHY[newRoleIndex];
    const isOwnerPromotion = newRole === "Owner" && member.role === "Admin";
    const isCurrentUserTarget = targetUserId === Number(currentUserId);
    const isCurrentUserDemotedByTransfer = isOwnerPromotion; // Only Owner becomes Admin

    setIsProcessingAction(true);

    try {
      await axios.put(
        `${API_BASE_URL}/clan/${clanId}/members/${member.member_id}/role`,
        { role: newRole },
        { withCredentials: true },
      );

      if (isCurrentUserTarget || isCurrentUserDemotedByTransfer) {
        await refreshUserData();
      }

      setActionMessage({
        type: "success",
        message: `${member.username} successfully ${action}d to ${newRole}.`,
      });

      const updatedMembers = members.map((m) => {
        const isTarget = Number(m.member_id) === targetUserId;
        const isCurrentOwner =
          isOwnerPromotion && Number(m.member_id) === Number(currentUserId);

        if (isTarget) {
          return { ...m, role: newRole };
        } else if (isCurrentOwner) {
          return { ...m, role: "Admin" };
        } else {
          return m;
        }
      });
      updateMembersList(updatedMembers);
    } catch (err) {
      console.error(`${action} member failed:`, err);
      const errorMessage = getErrorMessage(
        err,
        `Failed to ${action} member. Please check hierarchy rules.`,
      );
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const fetchAvailableMembers = async () => {
    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/available_members`;
      const response = await axios.get(endpoint, { withCredentials: true });

      const options = response.data.available_users.map((user) => {
        const igns = user.osrs_igns || [];
        let displayIgn = "No IGN Linked";

        if (igns.length > 0) {
          const limit = 3;
          const visibleIgns = igns.slice(0, limit).join(", ");
          displayIgn =
            igns.length > limit
              ? `${visibleIgns} (+${igns.length - limit} more)`
              : visibleIgns;
        }

        return {
          value: user.id,
          label: user.username,
          osrs_ign: displayIgn,
        };
      });

      setAvailableMembers(options);
    } catch (err) {
      console.error("Error fetching available members:", err);
    }
  };

  const formatOptionLabel = ({ label, osrs_ign }) => {
    const isNoIgn = osrs_ign === "No IGN Linked";
    
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{label}</span>
        {osrs_ign && (
          <small
            style={{
              color: isNoIgn ? "#64748b" : "#94a3b8",
              fontSize: "0.75rem",
              backgroundColor: "#1e1e1e",
              padding: "2px 6px",
              borderRadius: "4px",
              marginLeft: "10px",
              fontStyle: isNoIgn ? "italic" : "normal",
            }}
          >
            {isNoIgn ? osrs_ign : `IGN: ${osrs_ign}`}
          </small>
        )}
      </div>
    );
  };

  const handleAddMemberClick = () => {
    setNewMemberData({ user_id: "", role: "Guest" });
    setSelectedNewMemberOption(null);
    fetchAvailableMembers();
    setIsAddMemberModalOpen(true);
  };

  const handleMemberSelect = (selectedOption) => {
    setSelectedNewMemberOption(selectedOption);
    setNewMemberData((prevData) => ({
      ...prevData,
      user_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const executeAddMember = async () => {
    setIsAddMemberModalOpen(false);
    setActionMessage(null);

    if (!newMemberData.user_id || !selectedNewMemberOption) return;

    setIsProcessingAction(true);

    try {
      await axios.post(
        `${API_BASE_URL}/clan/${clanId}/members`,
        newMemberData,
        { withCredentials: true },
      );

      const newMember = {
        member_id: newMemberData.user_id,
        username: selectedNewMemberOption.label,
        role: newMemberData.role,
        joined_at: new Date().toISOString(),
        last_login: null,
        osrs_igns:
          selectedNewMemberOption.osrs_ign === "No IGN Linked"
            ? []
            : selectedNewMemberOption.osrs_ign.split(", "),
      };

      setActionMessage({
        type: "success",
        message: `User (Username: ${selectedNewMemberOption.label}) added as ${newMemberData.role}.`,
      });

      updateMembersList([...members, newMember]);

      setNewMemberData({ user_id: "", role: "Guest" });
      setSelectedNewMemberOption(null);
    } catch (err) {
      console.error("Add member failed:", err);
      const errorMessage = getErrorMessage(err, "Failed to add member.");
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [members.length, pageSize, searchTerm]);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;

    const lowerCaseSearch = searchTerm.toLowerCase();
    return members.filter((member) => {
      const usernameMatch = member.username
        .toLowerCase()
        .includes(lowerCaseSearch);
      const roleMatch = member.role.toLowerCase().includes(lowerCaseSearch);

      const ignMatch = (member.osrs_igns || []).some((ign) =>
        ign.toLowerCase().includes(lowerCaseSearch),
      );

      return usernameMatch || roleMatch || ignMatch;
    });
  }, [members, searchTerm]);

  const totalPages = Math.ceil(filteredMembers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentMembersPage = filteredMembers.slice(startIndex, endIndex);

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  const resultsCountMessage = useMemo(() => {
    const totalCount = members.length;
    const filteredCount = filteredMembers.length;

    if (filteredCount === 0) return "No members found";

    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(start + pageSize - 1, filteredCount);

    let message = `${start}-${end} of ${filteredCount} members`;

    if (filteredCount < totalCount) {
      message += ` (Filtered from ${totalCount})`;
    }
    return message;
  }, [members.length, filteredMembers.length, currentPage, pageSize]);

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages || totalPages === 0;

  const renderActions = (member) => {
    const isSelf = Number(member.member_id) === Number(currentUserId);
    const isSelfManager = member.role === "Admin" || member.role === "Owner";

    if (isSelf) {
      const canLeave = !isSelfManager || totalManagers > 1;
      const leaveTitle = !canLeave
        ? "As the sole manager, you must use the Disband Clan button on the Manage Clan page."
        : "";

      return (
        <button
          className="btn-small btn-leave"
          onClick={handleLeaveClan}
          disabled={!canLeave || isActionInProgress}
          title={leaveTitle}
        >
          Leave Clan
        </button>
      );
    }

    if (!isCurrentUserManager) {
      return null;
    }

    const isTargetGuest = member.role === "Guest";
    const isTargetMember = member.role === "Member";
    const isTargetAdmin = member.role === "Admin";
    const isTargetOwner = member.role === "Owner";

    const canPromote =
      isTargetGuest || isTargetMember || (isTargetAdmin && isCurrentUserOwner);

    const canDemote =
      (isTargetOwner && isCurrentUserOwner) || isTargetAdmin || isTargetMember;

    const canKick =
      isTargetGuest || isTargetMember || (isTargetAdmin && isCurrentUserOwner);

    const isOwnerTransfer = isTargetAdmin && isCurrentUserOwner;
    const promoteButtonText = isOwnerTransfer
      ? "Transfer Ownership"
      : "Promote";
    const promoteButtonClass = isOwnerTransfer
      ? "btn-small btn-owner-transfer"
      : "btn-small btn-promote";
    const promoteTitle = isOwnerTransfer
      ? "Transfer Owner role to this Admin, you will become an Admin."
      : isTargetOwner
        ? "Cannot promote an Owner."
        : isTargetGuest || isTargetMember
          ? ""
          : "";

    const demoteTitle = isTargetGuest ? "Cannot demote a Guest." : "";

    return (
      <div className="action-buttons-group">
        <button
          className="btn-small btn-demote"
          onClick={() => handleUpdateRole(member, "demote")}
          disabled={!canDemote || isActionInProgress}
          title={!canDemote && isTargetGuest ? demoteTitle : ""}
        >
          Demote
        </button>

        <button
          className={`${promoteButtonClass}`}
          onClick={() => handleUpdateRole(member, "promote")}
          disabled={!canPromote || isActionInProgress}
          title={promoteTitle}
        >
          {promoteButtonText}
        </button>
        <button
          className="btn-small btn-remove"
          onClick={() => handleKickMember(member.member_id, member.username)}
          disabled={!canKick || isActionInProgress}
        >
          Kick
        </button>
      </div>
    );
  };

  return (
    <>
      {isCurrentUserManager && (
        <div className="members-tab-controls-top">
          <button
            className="btn-large btn-primary"
            onClick={handleAddMemberClick}
            disabled={isActionInProgress}
          >
            Add Member
          </button>
          <div className="search-bar-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by username or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isActionInProgress}
              className="search-input"
            />
          </div>
        </div>
      )}

      <table className="member-table">
        <thead>
          <tr>
            <th>Username (Discord)</th>
            <th>IGNs</th>
            <th>Role</th>
            <th>Joined</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentMembersPage.length > 0 ? (
            currentMembersPage.map((member) => {
              const igns = member.osrs_igns || [];
              let displayIgn = "None";
              let isNone = true;

              if (igns.length > 0) {
                isNone = false;
                const limit = 2;
                const visible = igns.slice(0, limit).join(", ");
                displayIgn =
                  igns.length > limit
                    ? `${visible} (+${igns.length - limit})`
                    : visible;
              }

              return (
                <tr key={member.member_id}>
                  <td data-label="Username (Discord)">{member.username}</td>
                  <td data-label="IGNs">
                    <span
                      className={`ign-table-cell ${igns.length === 0 ? "ign-none" : ""}`}
                      title={
                        igns.length > 0 ? igns.join(", ") : "No IGNs linked"
                      }
                    >
                      {displayIgn}
                    </span>
                  </td>
                  <td data-label="Role">
                    <span className={`role-${member.role.toLowerCase()}`}>
                      {member.role}
                    </span>
                  </td>
                  <td data-label="Joined">
                    {formatLocalDate(member.joined_at)}
                  </td>
                  <td data-label="Last Login">
                    {member.last_login
                      ? formatLocalDate(member.last_login)
                      : "N/A"}
                  </td>
                  <td data-label="Actions">{renderActions(member)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5" className="no-results">
                No members found matching "{searchTerm}".
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {filteredMembers.length > 0 && (
        <div className="pagination-controls">
          <div className="page-size-selector">
            <label htmlFor="pageSizeSelect">Items per page:</label>
            <select
              id="pageSizeSelect"
              value={pageSize}
              onChange={handlePageSizeChange}
              disabled={isActionInProgress}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="pagination-center-group">
            <div className="pagination-buttons">
              <button
                onClick={goToPrevPage}
                disabled={isPrevDisabled || isActionInProgress}
                className="btn-pagination"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={isNextDisabled || isActionInProgress}
                className="btn-pagination"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="results-count-display">{resultsCountMessage}</div>
        </div>
      )}

      <Modal
        isOpen={isLeaveModalOpen}
        title="Confirm Leave Clan"
        message={`Are you sure you want to leave ${clanName}? If you are a manager, the role will be automatically transferred to another eligible member (or the next highest rank).`}
        onConfirm={executeLeaveClan}
        onCancel={() => setIsLeaveModalOpen(false)}
        confirmButtonText="Yes, Leave Clan"
        confirmDisabled={isActionInProgress}
      />

      <Modal
        isOpen={isKickModalOpen}
        title="Confirm Member Kick"
        message={`You are about to remove ${
          targetMember?.username || "this member"
        } from the clan. This action cannot be undone.`}
        onConfirm={executeKick}
        onCancel={() => {
          setIsKickModalOpen(false);
          setTargetMember(null);
        }}
        confirmButtonText="Confirm Kick"
        confirmDisabled={isActionInProgress}
      />

      <Modal
        isOpen={isAddMemberModalOpen}
        title="Add New Clan Member"
        message={
          <div className="add-member-form-container">
            <p style={{ marginBottom: "10px" }}>
              Search for a user's Discord username who is not currently in this
              clan:
            </p>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              <label htmlFor="user_id" className="modal-form-label">
                Select User:
              </label>
              <Select
                id="user_id"
                classNamePrefix="select"
                value={selectedNewMemberOption}
                onChange={handleMemberSelect}
                options={availableMembers}
                placeholder="Search by Discord or IGN..."
                isClearable
                styles={customSelectStyles}
                isDisabled={availableMembers.length === 0 || isActionInProgress}
                formatOptionLabel={formatOptionLabel}
                getOptionValue={(option) =>
                  `${option.label} ${option.osrs_ign || ""}`
                }
                filterOption={(option, rawInput) => {
                  const input = rawInput.toLowerCase();
                  return (
                    option.data.label.toLowerCase().includes(input) ||
                    (option.data.osrs_ign &&
                      option.data.osrs_ign.toLowerCase().includes(input))
                  );
                }}
                noOptionsMessage={() =>
                  availableMembers.length === 0
                    ? "No available users found."
                    : "No matching users found."
                }
              />

              <label
                htmlFor="role"
                className="modal-form-label"
                style={{ marginTop: "15px" }}
              >
                Assign Role:
              </label>
              <select
                id="role"
                className="modal-form-field"
                value={newMemberData.role}
                onChange={(e) =>
                  setNewMemberData({ ...newMemberData, role: e.target.value })
                }
                disabled={isActionInProgress}
              >
                <option value="Guest">Guest</option>
                <option value="Member">Member</option>
                {isCurrentUserManager && <option value="Admin">Admin</option>}
              </select>
            </div>
          </div>
        }
        onConfirm={executeAddMember}
        onCancel={() => setIsAddMemberModalOpen(false)}
        confirmButtonText="Add Member"
        confirmDisabled={!newMemberData.user_id || isActionInProgress}
      />
    </>
  );
}

export default ClanMembersTab;
